/**
 * HL-10959 / HL-10694 — Care+ vs legacy weight-loss experiment props.
 *
 * Assignment is owned by the CMS allocator (`wl-care-plus-ab-allocate.ejs`) on
 * the DE weight-loss category page and Mounjaro/Wegovy product pages. The funnel
 * only reads the sticky localStorage keys and registers Mixpanel super-props so
 * every subsequent WL event (through Paid Order) carries the arm + entry point.
 *
 * Storage (shared with CMS):
 *   wl_new_design_ab_variant   → 'control' | 'test'
 *   wl_experiment_entry_point  → 'category' | 'mounjaro' | 'wegovy'
 *
 * Analytics mapping (parent ticket HL-10694):
 *   control → wl_experiment: 'legacy'
 *   test    → wl_experiment: 'm1'
 */

import { MainTracker } from "../PixelTrackers/MainTracker";

export const WL_AB_VARIANT_KEY = "wl_new_design_ab_variant";
export const WL_ENTRY_POINT_KEY = "wl_experiment_entry_point";

export type WlAbVariant = "control" | "test";
export type WlExperimentArm = "legacy" | "m1";
/** First-touch page: `category` or product catalog mention (e.g. mounjaro, wegovy). */
export type WlEntryPoint = string;

export type WlExperimentProps = {
  wl_experiment: WlExperimentArm;
  entry_point?: WlEntryPoint;
};

// category | product mention from catalogs.settings opt-in (mention slug)
const ENTRY_POINT_RE = /^[a-z][a-z0-9_]{0,63}$/i;

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Map sticky CMS variant → analytics arm. Null if visitor was never bucketed. */
export function getWlExperimentArm(): WlExperimentArm | null {
  const variant = readStorage(WL_AB_VARIANT_KEY);
  if (variant === "test") return "m1";
  if (variant === "control") return "legacy";
  return null;
}

export function getWlEntryPoint(): WlEntryPoint | null {
  const ep = readStorage(WL_ENTRY_POINT_KEY);
  if (ep && ENTRY_POINT_RE.test(ep)) return ep;
  return null;
}

/** Props object for event merges (Paid Order etc.). Empty object if unbucketed. */
export function getWlExperimentTrackingProps(): Partial<WlExperimentProps> {
  const arm = getWlExperimentArm();
  if (!arm) return {};
  const entry_point = getWlEntryPoint();
  return entry_point ? { wl_experiment: arm, entry_point } : { wl_experiment: arm };
}

/**
 * Register WL experiment super-props on Mixpanel once the visitor has been
 * bucketed on a CMS entry page. Safe to call repeatedly; no-ops if unbucketed.
 */
export function registerWlExperimentSuperProperties(): void {
  const props = getWlExperimentTrackingProps();
  if (!props.wl_experiment) return;
  MainTracker.superProperties(props);
}
